import React, { useEffect, useRef, useState } from "react";

// API
import { GetLatestAuthorInstagramReel } from "../../Api/AuthorInstagram";

// Assets
import IconClose from "../../Assets/SVGs/Icons/UI/Close";

// Context
import { PopupType, usePopup } from "../../Context/PopupContext";

// Helpers
import {
    getInstagramProfileUrl,
    getInstagramReelEmbedUrl,
    getSafeInstagramUrl,
} from "../../Helpers/Instagram";

// Models
import PublicAuthorDTO from "../../Models/DTOs/PublicAuthorDTO";
import { InstagramReelDTO } from "../../Models/DTOs/LatestInstagramReelDTO";

type PresenterReelData = PublicAuthorDTO;

const FrontPresenterReelPopup: React.FC = () => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const [latestReel, setLatestReel] = useState<InstagramReelDTO | null>(null);
    const [reelLoading, setReelLoading] = useState(false);
    const [reelError, setReelError] = useState("");
    const { closePopupIfType, isPopupOpen, popupData } = usePopup();
    const isOpen = isPopupOpen(PopupType.FRONT_PRESENTER_REEL);
    const presenter = (isOpen ? popupData : null) as PresenterReelData | null;
    const authorId = presenter?.Id;
    const instagramHandle = presenter?.InstagramHandle?.trim();
    const reelOverride = presenter?.InstagramLatestReelUrl?.trim();

    const closeReel = () => {
        closePopupIfType(PopupType.FRONT_PRESENTER_REEL);
    };

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        if (!isOpen) {
            if (dialog.open) dialog.close();
            return;
        }

        const previousBodyOverflow = document.body.style.overflow;
        if (!dialog.open) dialog.showModal();
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            if (dialog.open) dialog.close();
        };
    }, [isOpen]);

    useEffect(() => {
        return () => closePopupIfType(PopupType.FRONT_PRESENTER_REEL);
    }, [closePopupIfType]);

    useEffect(() => {
        setLatestReel(null);
        setReelError("");
        setReelLoading(false);

        if (!isOpen || !authorId) return;

        const safeOverride = getInstagramReelEmbedUrl(reelOverride)
            ? getSafeInstagramUrl(reelOverride)
            : null;
        if (safeOverride) {
            setLatestReel({
                id: null,
                permalink: safeOverride,
                caption: null,
                thumbnailUrl: null,
                publishedAt: null,
            });
            return;
        }
        if (!instagramHandle) return;

        const abortController = new AbortController();
        let isCancelled = false;
        setReelLoading(true);

        GetLatestAuthorInstagramReel(authorId, abortController)
            .then((response) => {
                if (isCancelled) return;
                if (!getInstagramReelEmbedUrl(response.reel?.permalink)) {
                    setReelError("This reel cannot be embedded right now.");
                    return;
                }
                setLatestReel(response.reel);
            })
            .catch(() => {
                if (!isCancelled) {
                    setReelError("The latest reel is unavailable right now.");
                }
            })
            .finally(() => {
                if (!isCancelled) setReelLoading(false);
            });

        return () => {
            isCancelled = true;
            abortController.abort();
        };
    }, [authorId, instagramHandle, isOpen, reelOverride]);

    if (!presenter) return null;

    const name = presenter.Name?.trim() || `Presenter ${presenter.Id}`;
    const handle = instagramHandle
        ? `@${instagramHandle.replace(/^@/, "")}`
        : null;
    const profileUrl =
        getSafeInstagramUrl(presenter.InstagramUrl) ||
        getInstagramProfileUrl(instagramHandle);
    const reelUrl = getSafeInstagramUrl(latestReel?.permalink);
    const embedUrl = getInstagramReelEmbedUrl(latestReel?.permalink);
    const portrait = presenter.Avatar?.trim() || presenter.HeaderImg?.trim();
    const fallbackImage = latestReel?.thumbnailUrl?.trim() || portrait;

    return (
        <dialog
            ref={dialogRef}
            className="front-presenter-reel-modal"
            aria-labelledby="front-presenter-reel-title"
            aria-describedby="front-presenter-reel-description"
            onClose={closeReel}
            onClick={(event) => {
                if (event.target === event.currentTarget) closeReel();
            }}
        >
            <div className="front-presenter-reel-modal__dialog">
                <button
                    className="front-presenter-reel-modal__close"
                    type="button"
                    aria-label="Close reel"
                    onClick={closeReel}
                >
                    <IconClose iconFill="white" />
                </button>

                <div className="front-presenter-reel-modal__media">
                    {embedUrl ? (
                        <iframe
                            src={embedUrl}
                            title={`${name} latest Instagram reel`}
                            loading="lazy"
                            allow="encrypted-media; fullscreen; picture-in-picture"
                            allowFullScreen
                            referrerPolicy="strict-origin-when-cross-origin"
                            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
                        />
                    ) : (
                        <div
                            className="front-presenter-reel-modal__fallback"
                            style={
                                fallbackImage
                                    ? { backgroundImage: `url("${fallbackImage}")` }
                                    : undefined
                            }
                        >
                            <span role="status">
                                {reelLoading
                                    ? "Finding the latest reel\u2026"
                                    : reelError || "Reel preview unavailable"}
                            </span>
                        </div>
                    )}
                </div>

                <div className="front-presenter-reel-modal__copy">
                    <p>Presenter reel</p>
                    <h2 id="front-presenter-reel-title">{name}</h2>
                    {handle ? <strong>{handle}</strong> : null}
                    <p id="front-presenter-reel-description">
                        Watch this presenter&apos;s latest available Instagram reel
                        without losing your place on Ickonic.
                    </p>

                    <div className="front-presenter-reel-modal__actions">
                        {reelUrl ? (
                            <a href={reelUrl} target="_blank" rel="noreferrer">
                                View original on Instagram
                            </a>
                        ) : profileUrl ? (
                            <a href={profileUrl} target="_blank" rel="noreferrer">
                                View profile on Instagram
                            </a>
                        ) : null}
                        <button type="button" onClick={closeReel}>
                            Return to Ickonic
                        </button>
                    </div>
                </div>
            </div>
        </dialog>
    );
};

export default FrontPresenterReelPopup;
